import type {
  ImportOptions,
  ImportPreview,
  ImportResult,
  ValueListItemInput,
  ValueListItemListResponse,
  ValueListItemPatch,
  ValueListItemQuery,
  ValueListCreate,
  ValueListItemView,
  ValueListSummary,
  ValueListUpdate,
} from '@messe/shared'
import { apiRequest } from './api.js'

export function listValueLists(): Promise<ValueListSummary[]> {
  return apiRequest<ValueListSummary[]>('/value-lists')
}

export function createValueList(input: ValueListCreate): Promise<ValueListSummary> {
  return apiRequest<ValueListSummary>('/value-lists', { method: 'POST', body: input })
}

export function updateValueList(id: string, changes: ValueListUpdate): Promise<ValueListSummary> {
  return apiRequest<ValueListSummary>(`/value-lists/${id}`, { method: 'PATCH', body: changes })
}

export function listValueListItems(
  listId: string,
  query: Partial<ValueListItemQuery> = {},
): Promise<ValueListItemListResponse> {
  const params = new URLSearchParams()
  if (query.search) params.set('search', query.search)
  if (query.status) params.set('status', query.status)
  if (query.activeOnly === false) params.set('activeOnly', 'false')
  if (query.sort) params.set('sort', query.sort)
  if (query.direction) params.set('direction', query.direction)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))

  const suffix = params.size > 0 ? `?${params.toString()}` : ''
  return apiRequest<ValueListItemListResponse>(`/value-lists/${listId}/items${suffix}`)
}

export function createValueListItem(
  listId: string,
  input: ValueListItemInput,
): Promise<ValueListItemView> {
  return apiRequest<ValueListItemView>(`/value-lists/${listId}/items`, {
    method: 'POST',
    body: input,
  })
}

export function updateValueListItem(
  listId: string,
  itemId: string,
  changes: ValueListItemPatch,
): Promise<ValueListItemView> {
  return apiRequest<ValueListItemView>(`/value-lists/${listId}/items/${itemId}`, {
    method: 'PATCH',
    body: changes,
  })
}

export interface MergeResponse {
  sourceId: string
  targetId: string
  repointed: { itemSupplier: number; itemAttributes: number; users: number }
}

export function mergeValueListItems(
  listId: string,
  sourceId: string,
  targetId: string,
): Promise<MergeResponse> {
  return apiRequest<MergeResponse>(`/value-lists/${listId}/merge`, {
    method: 'POST',
    body: { sourceId, targetId },
  })
}

export interface PendingEntry extends ValueListItemView {
  valueListKey: string
  valueListName: string
}

export function listPendingEntries(): Promise<PendingEntry[]> {
  return apiRequest<PendingEntry[]>('/value-lists/pending')
}

export async function approveEntry(
  listId: string,
  itemId: string,
  changes: { label?: string; value?: string; metadata?: Record<string, unknown> } = {},
): Promise<void> {
  await apiRequest<undefined>(`/value-lists/${listId}/items/${itemId}/approve`, {
    method: 'POST',
    body: changes,
  })
}

export function rejectEntry(
  listId: string,
  itemId: string,
  reason?: string,
): Promise<{ referencingItems: number }> {
  return apiRequest<{ referencingItems: number }>(`/value-lists/${listId}/items/${itemId}/reject`, {
    method: 'POST',
    body: reason === undefined ? {} : { reason },
  })
}

// --- CSV import --------------------------------------------------------------

/**
 * Reads a file into base64.
 *
 * The API takes uploads in the JSON body rather than as multipart, so the file
 * has to be encoded here. FileReader rather than a manual loop over the bytes:
 * a large file built up through string concatenation blocks the main thread
 * long enough to freeze the page.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => {
      reject(new Error('Die Datei konnte nicht gelesen werden'))
    }
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Die Datei konnte nicht gelesen werden'))
        return
      }
      // Strips the `data:...;base64,` prefix.
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })
}

export function previewImport(
  listId: string,
  input: {
    fileName: string
    contentBase64: string
    encoding?: ImportOptions['encoding']
    delimiter?: string
    hasHeaderRow: boolean
  },
): Promise<ImportPreview> {
  return apiRequest<ImportPreview>(`/value-lists/${listId}/import/preview`, {
    method: 'POST',
    body: input,
  })
}

export function runImport(
  listId: string,
  input: { fileName: string; contentBase64: string; settings: ImportOptions },
): Promise<ImportResult> {
  return apiRequest<ImportResult>(`/value-lists/${listId}/import`, {
    method: 'POST',
    body: input,
  })
}

/**
 * Metadata fields shown as their own columns for suppliers.
 *
 * Kept here rather than derived from the schema so the order is a presentation
 * decision, which is what it is.
 */
export const SUPPLIER_METADATA_FIELDS = [
  { key: 'supplierNumber', labelKey: 'supplierNumber' },
  { key: 'country', labelKey: 'country' },
  { key: 'contact', labelKey: 'contact' },
  { key: 'note', labelKey: 'note' },
] as const
